import { stores } from '@kurtosys/app-start';
import { components } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { IQuestion } from '@kurtosys/types/learning/IQuestion';
import { TAnswers } from '@kurtosys/types/learning/TAnswers';
import { action, computed, makeObservable } from 'mobx';

import { IComponentStyles } from '../../../models/app/IComponentStyles';
import { IConfiguration } from '../../../models/app/IConfiguration';
import { TStoreContext } from '../../../models/app/TStoreContext';
import { AppStore } from '../../App/stores/AppStore';
import { QuizStore } from '../../App/stores/QuizStore';
import { IQuestionConfiguration } from '../models';
type IRadioButtonsProps = components.base.RadioButtons.models.IRadioButtonsProps;
type ITextProps = components.base.Text.models.ITextProps;

const StoreBase = stores.base.StoreBase<IConfiguration, IComponentStyles>;

export class QuestionStore extends StoreBase {
	static componentKey = 'question' as const;

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);
		makeObservable(this);
	}

	@computed
	get appStore() {
		return this.storeContext.get<AppStore>('appStore');
	}

	@computed
	get quizStore() {
		return this.storeContext.get<QuizStore>('quizStore');
	}

	@computed
	get configuration(): IQuestionConfiguration | undefined {
		if (this.storeContext && this.appStore) {
			return this.appStore.getComponentConfiguration(QuestionStore.componentKey);
		}
	}

	@action
	async initialize(): Promise<void> {
		return;
	}

	@computed
	get question(): IQuestion | undefined {
		if (this.quizStore.questions.length > 0) {
			return this.quizStore.questions[this.quizStore.currentQuestion];
		}
	}

	@computed
	get answers(): TAnswers {
		const answers = this.question && this.question.answers;
		return !common.commonUtils.isNullOrEmpty(answers)
			? answers
			: [
					{
						answer: 'No answers have been defined for this question',
					},
			  ];
	}

	@computed
	get userAnswer(): number | undefined {
		if (this.question) {
			return this.quizStore.userAnswers[this.quizStore.currentQuestionId];
		}
	}

	@computed
	get textProps(): ITextProps {
		return {
			value: (this.question && this.question.name) || '',
			hasHtmlContent: (this.configuration && this.configuration.allowHtmlContent) || false,
		};
	}

	@computed
	get answerPromptProps(): ITextProps {
		return {
			value: (this.configuration && this.configuration.answerPrompt) || 'Please select an answer:',
		};
	}

	@computed
	get radioButtonsProps(): IRadioButtonsProps {
		// If the user has not answered the question this.userAnswer will return undefined,
		// we need to explicitly set the value to empty string to reset the radio button value
		return {
			value: this.userAnswer ?? '',
			hasHtmlContent: (this.configuration && this.configuration.allowHtmlContent) || false,
			options: this.answers.map((answer, index) => {
				return {
					value: index,
					label: answer.answer,
				};
			}),
			onChange: (value: number) => {
				if (this.question) {
					this.quizStore.answerQuestion(value);
				}
			},
		};
	}
}
